import type { Root, SavedView } from "../../domain/app-types.ts";
import type { Category, PlanningCycle, TaskTemplate, Todo } from "../../domain/entities.ts";
import type { RecentTask } from "../navigation/qol-preferences.ts";
import type { CommandPaletteItem } from "./controller.ts";

export interface CommandCatalogState {
  activeRootId: string | null;
  activeRootName: string;
  activeCycleId: string | null;
  cycles: PlanningCycle[];
  selectedTask?: Todo;
  canEditSelected?: boolean;
  recentTasks: RecentTask[];
  roots: Root[];
  categories: Category[];
  savedViews: SavedView[];
  templates: TaskTemplate[];
  tasks: Todo[];
}

export interface CommandCatalogActions {
  newTask(): void | Promise<void>;
  quickNotes(createNew: boolean): void | Promise<void>;
  dailyNote?(): void | Promise<void>;
  browserCapture?(): void | Promise<void>;
  openView(view: "calendar" | "backlog" | "cycle"): void | Promise<void>;
  manageCycles(): void | Promise<void>;
  advancedFilters(): void | Promise<void>;
  createTemplate(): void | Promise<void>;
  dataManagement(): void | Promise<void>;
  notifications(): void | Promise<void>;
  editSelected(): void | Promise<void>;
  toggleSelectedComplete(): void | Promise<void>;
  toggleSelectedPin(): void | Promise<void>;
  openRecentTask(id: string): void | Promise<void>;
  openTask(id: string): void | Promise<void>;
  switchRoot(id: string): void | Promise<void>;
  openCategory(category: Category): void | Promise<void>;
  openSavedView(view: SavedView): void | Promise<void>;
  createFromTemplate(id: string): void | Promise<void>;
  discover?(): CommandPaletteItem[];
}

export function buildCommandCatalog(state: CommandCatalogState, actions: CommandCatalogActions): CommandPaletteItem[] {
  const selected = state.selectedTask;
  return [
    { id: "new", label: "Create new task", hint: "N", keywords: "add capture", run: actions.newTask },
    { id: "quick-notes", label: "Open quick notes", hint: "Q", run: () => actions.quickNotes(false) },
    ...(actions.dailyNote ? [{ id: "daily-note", label: "Open today’s daily note", hint: "Private", run: actions.dailyNote }] : []),
    ...(actions.browserCapture ? [{ id: "browser-capture", label: "Set up browser capture or capture a link", hint: "Private note", run: actions.browserCapture }] : []),
    { id: "quick-note-new", label: "Create quick note", hint: "Alt N", run: () => actions.quickNotes(true) },
    { id: "calendar", label: "Open calendar", hint: "", run: () => actions.openView("calendar") },
    { id: "backlog", label: "Open planning backlog", hint: "", run: () => actions.openView("backlog") },
    ...(state.activeCycleId ? [{ id: "cycle", label: `Open current cycle: ${state.cycles.find(cycle => cycle.id === state.activeCycleId)?.name || "Current cycle"}`, hint: "", run: () => actions.openView("cycle" as const) }] : []),
    { id: "cycles", label: "Manage planning cycles", hint: "", run: actions.manageCycles },
    { id: "filters", label: "Open advanced filters", hint: "", keywords: "search sort order save view", description: "Choose which tasks to show; task data is unchanged", run: actions.advancedFilters },
    { id: "template", label: "Manage task templates", hint: "", keywords: "create new edit reuse browse", run: actions.createTemplate },
    { id: "data", label: "Import, export, and backups", hint: "", run: actions.dataManagement },
    { id: "notifications", label: "Open notifications", hint: "", run: actions.notifications },
    ...(selected && state.canEditSelected !== false ? [
      { id: "edit-open-task", label: `Edit task: ${selected.title || "Untitled task"}`, hint: "Task action", run: actions.editSelected },
      { id: "complete-open-task", label: `${selected.completed ? "Reopen" : "Complete"} task: ${selected.title || "Untitled task"}`, hint: "Task action", run: actions.toggleSelectedComplete },
      { id: "pin-open-task", label: `${selected.pinned ? "Unpin" : "Pin"} task: ${selected.title || "Untitled task"}`, hint: "Task action", run: actions.toggleSelectedPin },
    ] : []),
    ...(actions.discover?.() || []),
    ...state.recentTasks.filter(task => state.tasks.some(item => item.id === task.id && !item.deleted)).map(task => ({ id: `recent-task-${task.id}`, label: `Recently viewed: ${task.title}`, group: "Recent" as const, hint: "", description: taskLocation(state.tasks.find(item => item.id === task.id)!, state), run: () => actions.openRecentTask(task.id) })),
    ...state.roots.filter(root => !root.archived_at).map(root => ({ id: `root-${root.id}`, label: `Switch to space: ${root.name}`, group: "Spaces" as const, hint: "", run: () => actions.switchRoot(root.id) })),
    ...state.categories.filter(category => !category.deleted && !category.archivedAt).map(category => ({ id: `category-${category.id}`, label: `Open collection: ${category.name}`, group: "Collections" as const, hint: "", description: rootName(category.rootId, state), run: () => actions.openCategory(category) })),
    ...state.savedViews.filter(view => !view.rootId || view.rootId === state.activeRootId).map(view => ({ id: `view-${view.id}`, label: `Open view: ${view.name}`, group: "Views" as const, hint: "", description: view.published ? "Shared with this space" : "Personal saved view", run: () => actions.openSavedView(view) })),
    ...state.templates.map(template => ({ id: `template-${template.id}`, label: `Create from template: ${template.name}`, group: "Templates" as const, hint: "", run: () => actions.createFromTemplate(template.id) })),
    ...state.tasks.filter(task => !task.deleted).map(task => ({ id: `task-${task.id}`, label: `Open task: ${task.title || "Untitled task"}`, group: "Tasks" as const, hint: "", description: taskLocation(task, state), run: () => actions.openTask(task.id) })),
  ];
}

function rootName(id: string, state: CommandCatalogState): string {
  return state.roots.find(root => root.id === id)?.name || (id === state.activeRootId ? state.activeRootName : "Shared with you");
}

function taskLocation(task: Todo, state: CommandCatalogState): string {
  const collection = state.categories.find(category => category.id === task.categoryId)?.name;
  return [rootName(task.rootId, state), collection, task.archivedAt ? "Archived" : task.completed ? "Completed" : ""].filter(Boolean).join(" → ");
}
